import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import PortalAxios from "../../service/portalAxios";
import {
  CreateUserArg,
  CreateUserResponse,
  MessageItem,
  MessageState,
} from "../../types/portal";

const initialState: MessageState = {
  message: { sent: [], received: [] },
  documents: { sent: [], received: [] },
  fetched_pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
  customerMessages: { sent: [], received: [] },
  customerDocuments: { sent: [], received: [] },
  loading: false,
  error: null,
  successMessage: null,
};

// Utility for API calls
const getBaseUrl = () => {
  if (!process.env.REACT_APP_BASE_URL) {
    throw new Error(
      "REACT_APP_BASE_URL is not defined in environment variables"
    );
  }
  return process.env.REACT_APP_BASE_URL;
};

const apiCall = async <T>(
  url: string,
  method: "get" | "post" | "patch" | "delete",
  data?: any
) => {
  try {
    const response = await PortalAxios[method](url, data);
    return response.data as T;
  } catch (err: any) {
    throw (
      err.response?.data || {
        message: `Failed to ${method.toUpperCase()} ${url}: ${err.message}`,
        code: err.response?.status,
      }
    );
  }
};

// Helper to categorize messages
const categorizeMessages = (
  items: MessageItem[]
): Pick<MessageState, "message" | "documents"> => {
  const result: Pick<MessageState, "message" | "documents"> = {
    message: { sent: [], received: [] },
    documents: { sent: [], received: [] },
  };

  if (!Array.isArray(items)) {
    console.warn("categorizeMessages called with non-array items", items);
    return result;
  }

  items.forEach((item) => {
    const isSent = item.SentByIFA === true;
    const target =
      item.IsDocumentMessage === true ? result.documents : result.message;
    (isSent ? target.sent : target.received).push(item);
  });

  return result;
};

// FETCH ALL SENT AND RECEIVED MESSAGES/DOCUMENTS (no ?SentByIFA param)
export const fetchAllDocumentMessage = createAsyncThunk<
  { data: MessageItem[] },
  { userId: string; page?: number; limit?: number },
  { rejectValue: { message: string; code?: number } }
>(
  "message/fetchAllDocumentMessage",
  async ({ userId, page, limit }, { rejectWithValue }) => {
    try {
      let api = `${getBaseUrl()}client/message/user/${userId}`;
      const params: string[] = [];
      if (page !== undefined) params.push(`page=${page}`);
      if (limit !== undefined) params.push(`limit=${limit}`);
      if (params.length > 0) {
        api += `?${params.join("&")}`;
      }
      const response = await apiCall<{ data: MessageItem[] }>(api, "get");
      return response;
    } catch (err: any) {
      return rejectWithValue({
        message: err?.message,
        code: err?.code,
      });
    }
  }
);

export const fetchCustomerMessages = createAsyncThunk<
  MessageItem[],
  { customerId: string },
  { rejectValue: string }
>(
  "message/fetchCustomerMessages",
  async ({ customerId }, { rejectWithValue }) => {
    const api = `${getBaseUrl()}client/message/customer/${customerId}`;
    try {
      const response = await PortalAxios.get(api);
      return response.data.data as MessageItem[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "An error occurred");
    }
  }
);

// POST MESSAGE
export const postMessage = createAsyncThunk<any, any>(
  "messages/postMessage",
  async (params, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append("customer_id", params?.customer_id);
    formData.append("subject", params?.subject);
    formData.append("message", params?.message);
    formData.append("sent_by_ifa", params?.sent_by_ifa);
    formData.append("isdocument_message", params?.isdocument_message);
    if (params?.file) {
      formData.append("file", params.file);
    }

    const api = `${getBaseUrl()}client/message`;

    try {
      const response = await PortalAxios.post(api, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// PATCH MESSAGE READ
export const patchMessage = createAsyncThunk<any, any>(
  "message/patchMessage",
  async (messageId, { rejectWithValue }) => {
    const api = `${getBaseUrl()}client/message/${messageId}/read`;
    try {
      return await apiCall(api, "patch");
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// DOWNLOAD DOCUMENT
export const downloadDocument = createAsyncThunk<
  { message: string },
  { messageId: string; documentId: string },
  { rejectValue: { message: string; code?: number } }
>(
  "message/downloadDocument",
  async ({ messageId, documentId }, { rejectWithValue }) => {
    const api = `${getBaseUrl()}client/message/${messageId}/document/${documentId}/download`;
    try {
      return await apiCall<{ message: string }>(api, "patch");
    } catch (err) {
      return rejectWithValue(err as { message: string; code?: number });
    }
  }
);

// DELETE MESSAGE
export const deleteMessage = createAsyncThunk<any, any>(
  "message/deleteMessage",
  async (messageId, { rejectWithValue }) => {
    const api = `${getBaseUrl()}client/message/${messageId}`;
    try {
      return await apiCall<{ message: string }>(api, "delete");
    } catch (err) {
      return rejectWithValue(err as { message: string; code?: number });
    }
  }
);

// CREATE CLIENT PORTAL USER
export const createUser = createAsyncThunk<
  CreateUserResponse,
  CreateUserArg,
  { rejectValue: { message: string; code?: number; data?: any } }
>("message/createUser", async ({ customerId }, { rejectWithValue }) => {
  const api = `${getBaseUrl()}client/client-portal-user/${customerId}`;
  try {
    const response = await apiCall<CreateUserResponse>(api, "post");
    return response;
  } catch (err) {
    return rejectWithValue(
      err as { message: string; code?: number; data?: any }
    );
  }
});

// DEACTIVATE CLIENT PORTAL USER
export const deactivateUser = createAsyncThunk<
  { message: string },
  { customerId: string },
  { rejectValue: { message: string; code?: number; data?: any } }
>("message/deactivateUser", async ({ customerId }, { rejectWithValue }) => {
  const api = `${getBaseUrl()}client/client-portal-user/${customerId}`;
  try {
    const response = await apiCall<{ message: string; isError?: boolean }>(
      api,
      "patch"
    );
    if (response.isError) {
      return rejectWithValue({ message: response.message });
    }
    return response;
  } catch (err: any) {
    const errorData = err?.response?.data || {
      message: err.message || "An error occurred",
      code: err.response?.status,
    };
    return rejectWithValue(errorData);
  }
});

const portalSlice = createSlice({
  name: "portal",
  initialState,
  reducers: {
    clearMessages(state) {
      state.message = { sent: [], received: [] };
      state.documents = { sent: [], received: [] };
      state.customerMessages = { sent: [], received: [] };
      state.customerDocuments = { sent: [], received: [] };
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---------- fetchAllDocumentMessage ----------
      .addCase(fetchAllDocumentMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(fetchAllDocumentMessage.fulfilled, (state, action: any) => {
        const categorized = categorizeMessages(action.payload.data);
        state.message = categorized.message;
        state.documents = categorized.documents;
        state.loading = false;
        state.successMessage =
          "All messages and documents fetched successfully";
        state.fetched_pagination = action.payload.pagination;
      })
      .addCase(fetchAllDocumentMessage.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload || {
          message: "Failed to fetch all messages",
        };
      })

      // ---------- fetchCustomerMessages ----------
      .addCase(fetchCustomerMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(fetchCustomerMessages.fulfilled, (state, action) => {
        const categorized = categorizeMessages(action.payload);
        state.customerMessages = {
          sent: categorized.message.sent,
          received: categorized.message.received,
        };
        state.customerDocuments = {
          sent: categorized.documents.sent,
          received: categorized.documents.received,
        };
        state.loading = false;
        state.successMessage = "Customer messages fetched successfully";
      })
      .addCase(fetchCustomerMessages.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload || {
          message: "Failed to fetch customer messages",
        };
      })

      // ---------- postMessage ----------
      .addCase(postMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(postMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        if (action.payload.data && !action.payload.data.IsDocumentMessage) {
          state.message.sent.unshift(action.payload.data);
        }
      })
      .addCase(postMessage.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload || { message: "Failed to post message" };
      })

      // ---------- deleteMessage ----------
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.successMessage = action.payload.message;
        const id = action.meta.arg.messageId;
        [
          "message",
          "documents",
          "customerMessages",
          "customerDocuments",
        ].forEach((key) => {
          (state as any)[key].sent = (state as any)[key].sent.filter(
            (msg: any) => msg.MessageId !== id
          );
          (state as any)[key].received = (state as any)[key].received.filter(
            (msg: any) => msg.MessageId !== id
          );
        });
      });
  },
});

export const { clearMessages } = portalSlice.actions;
export default portalSlice.reducer;
